import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Building, TrendingUp, Shield, Zap } from 'lucide-react';

const LAP = () => {
  const features = [
    {
      icon: <Building className="h-6 w-6" />,
      title: "High Loan Amount",
      description: "Get loans up to ₹5 crores against your property value"
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Competitive Rates",
      description: "Lower interest rates starting from 10% p.a. compared to personal loans"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Flexible Usage",
      description: "Use funds for business expansion, education, or any legitimate purpose"
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Quick Processing",
      description: "Fast approval and disbursal within 7-15 days"
    }
  ];

  const eligibility = [
    "Age: 25 to 70 years",
    "Property: Self-owned residential/commercial property",
    "Income: Minimum ₹50,000 per month (Salaried/Self-employed)",
    "Property Value: Minimum ₹15 lakhs",
    "CIBIL Score: 700 and above",
    "Property Age: Not more than 20-25 years"
  ];

  const documents = [
    "Property Documents (Title deed, Sale deed, Mutation)",
    "Property Tax Receipts and Khata Certificate",
    "Approved Plan and Completion Certificate",
    "Identity & Address Proof (Aadhaar/PAN/Passport)",
    "Income Documents (Salary slips/ITR/Business proof)",
    "Bank Statements (Last 6 months)",
    "Property Valuation Report",
    "NOC from Society/Builder (if applicable)"
  ];

  const usageTypes = [
    "Business Expansion",
    "Working Capital",
    "Debt Consolidation",
    "Medical Emergency",
    "Education Expenses",
    "Marriage Expenses",
    "Travel & Vacation",
    "Investment Opportunities"
  ];

  const propertyTypes = [
    "Residential Apartment/House",
    "Commercial Office/Shop",
    "Industrial Property",
    "Residential Plot",
    "Commercial Plot",
    "Warehouse/Godown"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                  Unlock Your Property's
                  <span className="block" style={{ color: '#340C7F' }}>
                    Hidden Value
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Get substantial funds against your property with competitive 
                  interest rates starting from 10% p.a. Perfect for business growth and personal needs.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>Substantial</p>
                  <p className="text-sm text-gray-600">Loan Amount</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>10%*</p>
                  <p className="text-sm text-gray-600">Interest Rate</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>Up to 20</p>
                  <p className="text-sm text-gray-600">Years Tenure</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>7-15 Days</p>
                  <p className="text-sm text-gray-600">Processing Time</p>
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white rounded-lg hover:opacity-90 transition-opacity shadow-lg"
                style={{ backgroundColor: '#340C7F' }}
              >
                Apply Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                alt="Loan Against Property" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Choose Our <span style={{ color: '#340C7F' }}>Loan Against Property?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center space-y-4 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full text-white" 
                     style={{ backgroundColor: '#340C7F' }}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Usage & Property Types */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Usage Types */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Flexible <span style={{ color: '#340C7F' }}>Fund Usage</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Use the loan amount for various purposes without any end-use restrictions.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {usageTypes.map((usage, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow">
                    <CheckCircle className="h-6 w-6 mx-auto mb-2" style={{ color: '#340C7F' }} />
                    <p className="text-sm font-medium text-gray-900">{usage}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Property Types */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Accepted <span style={{ color: '#340C7F' }}>Property Types</span>
                </h2>
                <p className="text-lg text-gray-600">
                  We accept various types of properties as collateral for the loan.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {propertyTypes.map((property, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <p className="font-medium text-gray-900">{property}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility & Documents Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Eligibility Criteria */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Eligibility <span style={{ color: '#340C7F' }}>Criteria</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Check if you meet our eligibility requirements for loan against property.
                </p>
              </div>

              <ul className="space-y-4">
                {eligibility.map((criterion, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{criterion}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Required Documents */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Required <span style={{ color: '#340C7F' }}>Documents</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Keep these documents ready for quick loan processing.
                </p>
              </div>

              <ul className="space-y-4">
                {documents.map((document, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{document}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Key <span style={{ color: '#340C7F' }}>Benefits</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Lower Interest Rates",
                description: "Enjoy significantly lower interest rates compared to unsecured loans",
                benefit: "Save up to 5-8% on interest"
              },
              {
                title: "High Loan to Value",
                description: "Get up to 75% of your property value as loan amount",
                benefit: "Maximum funding potential"
              },
              {
                title: "Longer Tenure",
                description: "Repay comfortably with tenure up to 20 years",
                benefit: "Lower EMI burden"
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 text-center shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 mb-4">{benefit.description}</p>
                <div className="inline-block px-4 py-2 rounded-full text-sm font-medium text-white" 
                     style={{ backgroundColor: '#340C7F' }}>
                  {benefit.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Use Cases Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Business <span style={{ color: '#340C7F' }}>Success Stories</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real businesses that grew with our Loan Against Property solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                business: "Manufacturing Unit",
                owner: "Suresh Patel",
                location: "Ahmedabad",
                loanAmount: "₹45 Lakhs",
                purpose: "Machinery Purchase",
                result: "Increased production capacity by 60%",
                story: "Used LAP to purchase new machinery and expand production facility. The lower interest rates compared to business loans helped maintain healthy cash flow."
              },
              {
                business: "Retail Chain",
                owner: "Meera Joshi",
                location: "Pune",
                loanAmount: "₹1.2 Crores",
                purpose: "Store Expansion",
                result: "Opened 5 new outlets",
                story: "Leveraged property value to expand retail operations across Maharashtra. The flexible repayment structure aligned perfectly with seasonal business cycles."
              },
              {
                business: "IT Services",
                owner: "Ravi Kumar",
                location: "Mumbai",
                loanAmount: "₹75 Lakhs",
                purpose: "Office Setup",
                result: "Team size increased from 20 to 80",
                story: "Established new corporate office and hired skilled professionals. The long tenure option kept EMIs manageable while scaling the business."
              },
              {
                business: "Restaurant Chain",
                owner: "Anita Sharma",
                location: "Mumbai",
                loanAmount: "₹65 Lakhs",
                purpose: "Multi-location Setup",
                result: "Revenue increased by 150%",
                story: "Opened 3 new restaurant locations using LAP. The quick approval process helped capitalize on prime location opportunities."
              }
            ].map((story, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">{story.business}</h3>
                    <span className="text-sm px-3 py-1 bg-white rounded-full text-gray-600">{story.location}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Loan Amount</p>
                      <p className="text-lg font-bold" style={{ color: '#340C7F' }}>{story.loanAmount}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Purpose</p>
                      <p className="text-sm text-gray-900">{story.purpose}</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-900 mb-2">Result Achieved:</p>
                    <p className="text-sm font-bold" style={{ color: '#340C7F' }}>{story.result}</p>
                  </div>
                  
                  <p className="text-sm text-gray-600 italic">"{story.story}"</p>
                  <p className="text-sm font-medium text-gray-900">- {story.owner}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Valuation Guide */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Property <span style={{ color: '#340C7F' }}>Valuation Guide</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding how we evaluate your property for maximum loan amount
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Valuation Factors</h3>
                <div className="space-y-6">
                  {[
                    { factor: "Location & Connectivity", weightage: "30%", description: "Proximity to main roads, metro, schools, hospitals" },
                    { factor: "Property Age & Condition", weightage: "25%", description: "Construction quality, maintenance, structural integrity" },
                    { factor: "Market Rates", weightage: "20%", description: "Recent sales in the area, market trends, appreciation potential" },
                    { factor: "Legal Documentation", weightage: "15%", description: "Clear title, approved plans, NOCs, tax compliance" },
                    { factor: "Amenities & Features", weightage: "10%", description: "Parking, security, facilities, accessibility" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: '#340C7F' }}>
                        {item.weightage}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{item.factor}</h4>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Loan-to-Value Ratio</h3>
                <div className="space-y-4">
                  {[
                    { type: "Residential Property", ltv: "75%", description: "Apartments, independent houses, villas" },
                    { type: "Commercial Property", ltv: "65%", description: "Offices, shops, showrooms" },
                    { type: "Industrial Property", ltv: "60%", description: "Warehouses, manufacturing units" }
                  ].map((item, index) => (
                    <div key={index} className="border-l-4 border-purple-500 pl-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">{item.type}</h4>
                        <span className="text-2xl font-bold" style={{ color: '#340C7F' }}>{item.ltv}</span>
                      </div>
                      <p className="text-sm text-gray-600">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border-2 border-purple-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Valuation Process</h3>
                <div className="space-y-3">
                  {[
                    "Property inspection by certified valuers",
                    "Document verification and legal check",
                    "Market research and comparative analysis",
                    "Technical evaluation report generation",
                    "Final valuation and loan amount determination"
                  ].map((step, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: '#340C7F' }}>
                        {index + 1}
                      </div>
                      <p className="text-sm text-gray-700">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#340C7F' }}>
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Ready to Unlock Your Property's Value?
          </h2>
          <p className="text-xl text-blue-100">
            Get substantial funding at attractive interest rates. Apply now for quick approval.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors shadow-lg transform hover:scale-105"
            >
              Apply for LAP
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-lg hover:bg-white hover:text-gray-900 transition-colors transform hover:scale-105"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LAP;